import React, { useState } from 'react';
import Modal from '~/components/organism/ModalsV2/Modal';
import Overlay from '~/components/organism/ModalsV2/Overlay';
import {
  useDeleteFlowV2Mutation,
  useUpdateFlowBlueprintDetailsMutation,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import type { ActionType } from '../../constants';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Button, { Props as ButtonProps } from '~/components/atom/Button';
import useAddToast from '~/hooks/useAddToast';
import TextButton from '~/components/atom/TextButton';
import executeBatchMutation from '../../utils/executeBatchMutation';
import { Body, Heading3 } from '~/components/atom/Typography';
import getFlowText from '../../utils/getFlowText';

const text = {
  cancelLabel: 'Annuleren',
  actions: {
    delete: 'Verwijder',
    activate: 'Activeer',
    deactivate: 'Deactiveer',
  },
  loading: {
    delete: 'Verwijderen...',
    activate: 'Activeren...',
    deactivate: 'Deactiveren...',
  },
  selectedFlows: 'geselecteerde flows',
};

const MAX_WIDTH = '600px';

type Props = {
  selectedItems: Array<string>;
  actionType: ActionType;
  onClose: (hasUpdates: boolean) => void;
};

const ActionModal: React.FCC<Props> = ({
  selectedItems,
  actionType,
  onClose,
}) => {
  const account = useCurrentAccount();
  const addToast = useAddToast();
  const [isLoading, setIsLoading] = useState(false);

  const [deleteFlow] = useDeleteFlowV2Mutation();
  const [updateFlowBlueprintDetails] = useUpdateFlowBlueprintDetailsMutation();

  const onDeleteSelectedFlows = async () => {
    await executeBatchMutation({
      selectedItems,
      actionType: 'delete',
      mutationFn: async id =>
        await deleteFlow({
          variables: { id: id, accountId: account.id },
        }),
      onClose: () => onClose(true),
      setIsLoading,
      addToast,
      getText: getFlowText,
    });
  };

  const onActivateSelectedFlows = async () => {
    await executeBatchMutation({
      selectedItems,
      actionType: 'activate',
      mutationFn: async id =>
        await updateFlowBlueprintDetails({
          variables: {
            id: id,
            accountId: account.id,
            update: { enabled: true },
          },
        }),
      onClose: () => onClose(true),
      setIsLoading,
      addToast,
      getText: getFlowText,
    });
  };

  const onDeactivateSelectedFlows = async () => {
    await executeBatchMutation({
      selectedItems,
      actionType: 'deactivate',
      mutationFn: async id =>
        await updateFlowBlueprintDetails({
          variables: {
            id: id,
            accountId: account.id,
            update: { enabled: false },
          },
        }),
      onClose: () => onClose(true),
      setIsLoading,
      addToast,
      getText: getFlowText,
    });
  };

  const buttonPropsByActionType: Record<
    ActionType,
    Pick<ButtonProps, 'label' | 'onClick' | 'appearance'>
  > = {
    delete: {
      appearance: 'danger',
      label: isLoading ? text.loading.delete : text.actions.delete,
      onClick: onDeleteSelectedFlows,
    },
    activate: {
      label: isLoading ? text.loading.activate : text.actions.activate,
      onClick: onActivateSelectedFlows,
      appearance: 'secondary',
    },
    deactivate: {
      label: isLoading ? text.loading.deactivate : text.actions.deactivate,
      onClick: onDeactivateSelectedFlows,
      appearance: 'danger',
    },
  };

  const onCloseModal = () => (isLoading ? undefined : onClose(false));

  return (
    <Overlay onClose={onCloseModal}>
      <Modal onClose={onCloseModal} maxWidth={MAX_WIDTH}>
        <JustificationContainer
          direction="column"
          width="100%"
          padding={['xl', 'xl', 'l', 'xl']}
        >
          <JustificationContainer width="100%" direction="column">
            <JustificationContainer width="100%">
              <Heading3>{`${text.actions[actionType]} ${text.selectedFlows}`}</Heading3>
            </JustificationContainer>
            <Body>{getBody(actionType, selectedItems.length)}</Body>
          </JustificationContainer>
          <JustificationContainer
            margin={['xl', null, null, null]}
            align="center"
            justification="space-between"
            width="100%"
          >
            <TextButton
              padding={[null]}
              label={text.cancelLabel}
              onClick={onCloseModal}
              size="medium"
              appearance="danger"
              disabled={isLoading}
            />
            <Button
              {...buttonPropsByActionType[actionType]}
              size="medium"
              disabled={isLoading}
              loading={isLoading}
            />
          </JustificationContainer>
        </JustificationContainer>
      </Modal>
    </Overlay>
  );
};

const getBody = (actionType: ActionType, selectedCount: number) => {
  switch (actionType) {
    case 'delete':
      return `Weet je zeker dat je ${selectedCount} ${getFlowText(
        selectedCount,
      )} wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.`;
    case 'activate':
      return `Weet je zeker dat je ${selectedCount} ${getFlowText(
        selectedCount,
      )} wilt activeren?`;
    case 'deactivate':
      return `Weet je zeker dat je ${selectedCount} ${getFlowText(
        selectedCount,
      )} wilt deactiveren?`;

    default:
      return null;
  }
};

export default ActionModal;
